import { deriveSymmetricKey, FPE, HPKE } from "open-signer-shared-cryptography";
import type { AttestationService } from "../attestation";
import type { FrameLogger } from "../logger";
import { CrossmintFrameService } from "../service";
import type { DeviceService } from "../user/device";

export class EncryptionService extends CrossmintFrameService {
	name = "Encryption service";
	log_prefix = "[EncryptionService]";

	constructor(
		protected readonly logger: FrameLogger,
		private readonly attestationService: AttestationService,
		private readonly deviceService: DeviceService,
		private readonly hpke = new HPKE(),
		private readonly fpe = new FPE(),
	) {
		super(logger);
	}

	/**
	 * Encrypts data for transmission TO the attested TEE.
	 *
	 * Uses HPKE base mode - the client acts as sender, TEE as recipient.
	 * No sender authentication is needed, user's authenticate their client devices
	 * and therefore the sender key, through other means, for example an OTP,
	 * This is handled outside the context of HPKE.
	 * The TEE's public key authenticity is guaranteed by hardware attestation.
	 *
	 * @param data - Data object to encrypt
	 * @returns Promise resolving to encryption result with ciphertext and encapsulated key
	 * @throws {Error} When encryption service is not initialized
	 * @throws {Error} When encryption operation fails
	 */
	public async encrypt<T extends Record<string, unknown>>(data: T) {
		return this.hpke.encryptBase64({
			data,
			recipientPublicKey: await this.attestationService.getAttestedPublicKey(),
			senderKeyPair: await this.deviceService.getIdentityKeys(),
			authMode: false,
		});
	}

	/**
	 * Decrypts messages received FROM the attested TEE.
	 *
	 * Uses HPKE auth mode to cryptographically verify that messages originated
	 * from the genuine attested TEE. This prevents impersonation attacks where
	 * malicious actors attempt to send fake messages claiming to be from the TEE.
	 *
	 * The sender verification happens automatically during HPKE decryption - if the
	 * message wasn't sent by the expected TEE (attested public key), decryption fails.
	 *
	 * @param ciphertextInput - Encrypted message data (string or ArrayBuffer)
	 * @param encapsulatedKeyInput - HPKE encapsulated key (string or ArrayBuffer)
	 * @param senderPublicKeyInput - Optional sender public key (defaults to attested TEE key)
	 * @returns Promise resolving to decrypted data
	 * @throws {Error} When encryption service is not initialized
	 * @throws {Error} When sender authentication fails (message not from expected TEE)
	 * @throws {Error} When decryption operation fails
	 */
	public async decrypt<
		T extends Record<string, unknown>,
		U extends string | ArrayBuffer,
	>(ciphertextInput: U, encapsulatedKeyInput: U): Promise<T> {
		const { data } = await this.hpke.decrypt<{ data: T }, U>({
			ciphertext: ciphertextInput,
			encapsulatedKey: encapsulatedKeyInput,
			recipientKeyPair: await this.deviceService.getIdentityKeys(),
			senderPublicKey: await this.attestationService.getAttestedPublicKey(),
		});
		return data;
	}

	/**
	 * Decrypts Format Preserving Encryption (FPE) data using a symmetric key derived from ECDH.
	 *
	 * This method performs FPE decryption using a symmetric encryption key that was created
	 * using Elliptic Curve Diffie-Hellman (ECDH) key exchange between:
	 * - **iframe's identity private key** (this user device's key pair)
	 * - **TEE's attested public key** (hardware-verified public key)
	 *
	 * The underlying key is derived via ECDH, ensuring both iframe and TEE can independently
	 * compute the same shared secret without network transmission. TEE authenticity is
	 * guaranteed by Intel TDX hardware attestation.
	 *
	 * @param data - Array of numbers representing the FPE-encrypted data
	 * @returns Promise resolving to the decrypted data
	 * @throws {Error} When encryption keys are not available
	 * @throws {Error} When FPE decryption operation fails
	 */
	public async decryptFPE(data: number[]) {
		const encryptionKeys = await this.deviceService.getIdentityKeys();
		const recipientPublicKey =
			await this.attestationService.getAttestedPublicKey();
		const key = await deriveSymmetricKey({
			privateKey: encryptionKeys.privateKey,
			publicKey: recipientPublicKey,
			permissions: ["decrypt"],
		});
		return this.fpe.decrypt({ data, key });
	}
}
